import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuditLogs, AuditFiltersPanel, AuditLogTable } from '../features/audit';
import type { AuditLogFilters } from '../features/audit';

export function AuditLogs() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [actionMessage, setActionMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const {
    logs,
    pagination,
    isLoading,
    error,
    availableActions,
    availableUsers,
    fetchLogs,
    fetchFilterOptions,
    exportLogs,
  } = useAuditLogs();

  useEffect(() => {
    fetchLogs(filters);
    fetchFilterOptions();
  }, [fetchLogs, fetchFilterOptions, filters]);

  const handleFiltersChange = useCallback((newFilters: AuditLogFilters) => {
    setFilters(newFilters);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({});
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchLogs(filters, page);
    },
    [fetchLogs, filters]
  );

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      await exportLogs(filters, format);
      setActionMessage({ type: 'success', text: t('audit.exportSuccess') });
      setTimeout(() => setActionMessage(null), 3000);
    } catch {
      setActionMessage({ type: 'error', text: t('audit.exportFailed') });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  if (isLoading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('audit.title')}</h1>
          <p className="text-muted-foreground">{t('audit.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="mr-2 h-4 w-4" />
            CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('json')}>
            <Download className="mr-2 h-4 w-4" />
            JSON
          </Button>
        </div>
      </div>

      {actionMessage && (
        <div
          className={`p-4 rounded-lg ${
            actionMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {actionMessage.text}
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">{error}</div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('audit.activityHistory')}</CardTitle>
              <CardDescription>
                {pagination.total} {t('audit.entries')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <AuditFiltersPanel
            filters={filters}
            availableActions={availableActions}
            availableUsers={availableUsers}
            onFiltersChange={handleFiltersChange}
            onReset={handleResetFilters}
          />

          <AuditLogTable logs={logs} pagination={pagination} onPageChange={handlePageChange} />
        </CardContent>
      </Card>
    </div>
  );
}
